import { AppIcon, HorizontalMenuTabs } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import {
  Box,
  Typography,
  SxProps,
  Theme,
  Button,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';

import { SectionHeader } from '../atoms/SectionHeader';
import { TABS, TabsConfig } from '../SettingsPage';

const ACCOUNT_SETTINGS_STRINGS = appStrings.modules.accountSettings;
const SETTINGS_STRINGS = appStrings.modules.accountSettings.settings;
const BILLING_STRINGS = appStrings.modules.accountSettings.billing;
const SEATS_STRINGS = appStrings.modules.accountSettings.seats;
const BUTTON_STRINGS = appStrings.common.button;

type SettingsHeaderProps = Readonly<{
  activeTab: number;
  tabConfig: TabsConfig;
  setActiveTab: (tab: number) => void;
  onReset?: () => void;
  onSubmit?: () => void;
  isLoading?: boolean;
  formSaveError?: string | null;
  hideSectionHeader?: boolean;
}>;

export const SettingsHeader = ({
  activeTab,
  tabConfig,
  setActiveTab,
  onReset,
  onSubmit,
  isLoading = false,
  formSaveError,
  hideSectionHeader = false,
}: SettingsHeaderProps) => {
  const tabs = tabConfig.tabs.map((tab) => tab.label);

  const disabledTabs = tabConfig.tabs.filter((tab) => tab.disabled).map((tab) => tab.label);

  // Get the appropriate strings based on active tab
  const getTabStrings = (tabIndex: number) => {
    switch (tabIndex) {
      case TABS.Settings:
        return SETTINGS_STRINGS.header;
      case TABS.Billing:
        return BILLING_STRINGS.header;
      case TABS.Seats:
        return SEATS_STRINGS.header;
      default:
        return ACCOUNT_SETTINGS_STRINGS.header;
    }
  };

  const currentTabStrings = getTabStrings(activeTab);

  return (
    <Box sx={containerSx}>
      <Typography variant="display-xs-semibold">{ACCOUNT_SETTINGS_STRINGS.header.title}</Typography>
      <Box sx={horizontalMenuSxLargerScreens}>
        <HorizontalMenuTabs
          id="settings-tabs"
          tabs={tabs}
          value={activeTab}
          setValue={setActiveTab}
          variant="box"
          disabledTabs={disabledTabs}
        />
      </Box>

      {/* SELECT FOR SMALL SCREENS */}
      <Box sx={phoneSizeScreenMenuDropdown}>
        <Select
          id="settings-tab-select"
          value={tabConfig.tabs[activeTab].id}
          defaultValue={tabs[0]}
          sx={selectSx}
          IconComponent={({ className }) => {
            const isSelectOpen = className.includes('MuiSelect-iconOpen');

            return (
              <Box sx={smallScreenHeaderSx(isSelectOpen)}>
                <AppIcon iconName="ChevronDown" diameter="16px" color="Gray/600" />
              </Box>
            );
          }}
        >
          {Object.entries(tabConfig.tabs).map(([tab, value]) => {
            return (
              <MenuItem
                key={tab}
                value={value.id}
                disabled={value.disabled}
                onClick={() => setActiveTab(Object.values(tabConfig.tabs).indexOf(value))}
              >
                <Typography variant="text-md-regular">{value.label}</Typography>
              </MenuItem>
            );
          })}
        </Select>
      </Box>

      {!hideSectionHeader && (
        <SectionHeader
          title={currentTabStrings.title}
          description={currentTabStrings.description}
          actions={
            (onReset || onSubmit) && (
              <Box sx={headerActionsContainerSx}>
                <Box sx={actionsContainerSx}>
                  {onReset && (
                    <Button
                      id="settings-cancel-button"
                      hierarchy="Secondary gray"
                      size="md"
                      onClick={onReset}
                      sx={buttonSx}
                    >
                      {BUTTON_STRINGS.cancel}
                    </Button>
                  )}
                  {onSubmit && (
                    <Button
                      id="settings-save-button"
                      hierarchy="Primary"
                      size="md"
                      onClick={onSubmit}
                      sx={buttonSx}
                    >
                      {/* when we are saving, instead of hiding the text we will set opacity to 0 so that the button does not change size */}
                      <Typography variant="text-md-semibold" sx={isLoading ? { opacity: 0 } : {}}>
                        {BUTTON_STRINGS.save}
                      </Typography>

                      {isLoading && <CircularProgress sx={loadingContainerSx} size={20} />}
                    </Button>
                  )}
                </Box>
                {formSaveError && (
                  <Typography
                    variant="text-sm-regular"
                    color={({ backrs }) => backrs.colors['Error/500']}
                  >
                    {formSaveError}
                  </Typography>
                )}
              </Box>
            )
          }
        />
      )}
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const headerActionsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  position: { xs: 'fixed', sm: 'static' },
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: backrs.colors['Colors/Base/White'],
  padding: { xs: backrs.spacing['spacing-2xl'], sm: backrs.spacing['spacing-none'] },
  borderTop: { xs: `1px solid ${backrs.colors['Gray/200']}`, sm: 'none' },
  zIndex: 10,
});

const actionsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: backrs.spacing['spacing-lg'],
});

const smallScreenHeaderSx =
  (isSelectOpen: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    transition: 'all .3s ease-in-out',
    mb: isSelectOpen ? backrs.spacing['spacing-xs'] : `-${backrs.spacing['spacing-xs']}`,
    transform: isSelectOpen ? 'rotate(180deg)' : 'rotate(0deg)',
  });

const phoneSizeScreenMenuDropdown: SxProps<Theme> = ({ breakpoints }) => ({
  [breakpoints.up('sm')]: {
    display: 'none',
  },
});

const selectSx: SxProps<Theme> = ({ backrs }) => ({
  paddingX: backrs.spacing['spacing-md'],
});

const horizontalMenuSxLargerScreens: SxProps<Theme> = {
  display: { xs: 'none', sm: 'block' },
};

const loadingContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',

  color: backrs.colors['Colors/Base/White'],

  position: 'absolute',
});

const buttonSx: SxProps<Theme> = ({ backrs }) => ({
  height: backrs.spacing['spacing-5xl'],
});
